import type { KbVisibleScope } from "@/entities/knowledge";
import type { DepartmentNode, OrgMemberWithDept } from "@/entities/org-department/api/orgService";
import { getPersona } from "@/shared/brand/persona";

/**
 * 「这份文档公司里谁看得到」的前端口径(2026-09-16)。
 *
 * 与 `kbVisibility.ts` 是**两根正交的轴**:那个文件判「AI 客服看不看得见」(对客),
 * 这个判「同事看不看得见」(对内)。列表上两个徽章并排,不是二选一。
 */

export const VISIBLE_SCOPE_LABEL: Record<KbVisibleScope, string> = {
  ALL: "全公司",
  DEPT: "指定部门",
  ADMIN: "仅管理员",
  OWNER: "仅自己",
};

/** 共享库里可选的三档 —— `OWNER` 不在此列:那是个人文档 tab 的形态,不从共享库这边往回塞。 */
export const SHARED_SCOPES: KbVisibleScope[] = ["ALL", "DEPT", "ADMIN"];

export const VISIBLE_SCOPE_HINT: Record<KbVisibleScope, string> = {
  get ALL() {
    return `公司所有同事都能看到和使用,AI ${getPersona().title}也会用它回答问题`;
  },
  DEPT: "只有选定部门及其下级部门的同事能看到",
  ADMIN: "只有企业管理员和你本人能看到,适合报价底线、成本、合同模板这类内容",
  OWNER: "只有你自己能看到",
};

/**
 * 「指定部门」这一档何时出现(B2 #23 读清并钉住):**启用中(enabled)的部门 ≥ 2**。
 * 只有一个根部门 = 组织还没建起来,选了也是空的(实测 33 个租户里只有 2 个建过部门树)。
 * ★企微同步来的部门若 `enabled=false`(未接管)不计入 —— QA 租户「2 根 + 1 子部门仍无此档」时先核
 *   `/tenant/departments` 返回的 enabled 值,判据本身没有别的分支。
 */
export function deptScopeAvailable(depts: ReadonlyArray<Pick<DepartmentNode, "enabled">>): boolean {
  return depts.filter((d) => d.enabled).length > 1;
}

/** 部门子树(含自身):物化 path 前缀匹配,与后端 `listSubtree` 同一口径。 */
export function subtreeDeptIds(depts: DepartmentNode[], rootId: number): Set<number> {
  const root = depts.find((d) => d.id === rootId);
  if (!root) return new Set([rootId]);
  const ids = new Set<number>([rootId]);
  for (const d of depts) {
    if (d.path && root.path && d.path.startsWith(root.path)) ids.add(d.id);
  }
  return ids;
}

export interface ScopeAudience {
  /** 能看到的人数;`null` = 算不出来(如 ADMIN 档:管理员是存储角色,不在组织成员表里)。 */
  count: number | null;
  /** 用于摘要的前几个名字。 */
  names: string[];
  /** 一句话摘要,直接渲染。 */
  summary: string;
}

/**
 * 把抽象档位翻译成**具体的人**。
 *
 * 这不是锦上添花:测试机实测每个租户恰好 1 个 MANAGER,而租户还能一键「全员设为负责人」——
 * 光看档位名字,客户以为自己配了一道部门墙,实际可能是 1 个人、也可能是全公司。
 * 人数和名单不骗人。
 */
export function describeAudience(
  scope: KbVisibleScope,
  deptId: number | null | undefined,
  depts: DepartmentNode[],
  members: OrgMemberWithDept[]
): ScopeAudience {
  const uniqueNames = (rows: OrgMemberWithDept[]) => {
    const seen = new Map<number, string>();
    for (const m of rows) if (!seen.has(m.principalId)) seen.set(m.principalId, m.displayName);
    return [...seen.values()];
  };

  if (scope === "ALL") {
    const names = uniqueNames(members);
    return {
      count: names.length,
      names,
      summary: names.length ? `全公司 ${names.length} 人可见` : "全公司可见",
    };
  }
  if (scope === "DEPT") {
    if (!deptId) return { count: null, names: [], summary: "请选择一个部门" };
    const ids = subtreeDeptIds(depts, deptId);
    const names = uniqueNames(members.filter((m) => ids.has(m.departmentId)));
    const deptName = depts.find((d) => d.id === deptId)?.name ?? "该部门";
    if (!names.length) {
      // 空部门是真实存在的形态(实测某租户「技术部门」0 人)—— 不提醒就等于把文档存进黑洞
      return { count: 0, names, summary: `${deptName}及其下级目前没有成员,只有管理员看得到` };
    }
    return {
      count: names.length,
      names,
      summary: `${deptName}及其下级 ${names.length} 人可见:${names.slice(0, 4).join("、")}${
        names.length > 4 ? " 等" : ""
      }`,
    };
  }
  if (scope === "ADMIN") {
    // 管理员是**存储角色**(手动任免),不在组织成员表里 —— 算不出人数就别编一个
    return { count: null, names: [], summary: "只有企业管理员和你本人可见" };
  }
  return { count: 1, names: [], summary: "只有你自己可见" };
}
